import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Employee } from '@/types';
import { useChangeEmployeeAdditionalPasswordMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface ChangeEmployeeAdditionalPasswordProps extends DialogProps {
  employee?: Employee;
  onClose(): void;
}

const ChangeEmployeeAdditionalPassword: React.FC<ChangeEmployeeAdditionalPasswordProps> = ({
  employee,
  onClose,
  ...props
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [changeEmployeeAdditionalPassword, changeEmployeeAdditionalPasswordResponse] =
    useChangeEmployeeAdditionalPasswordMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik({
    initialValues: { additional_password: '' },
    validationSchema: yup.object().shape({
      additional_password: yup.string().required(messages.form.REQUIRED).min(8, messages.form.MIN_LENGTH()),
    }),
    async onSubmit({ additional_password }, { resetForm }) {
      try {
        const response = await changeEmployeeAdditionalPassword({ id: employee?.id, additional_password }).unwrap();

        onClose();
        resetForm();
        enqueueSnackbar(response, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Сменить пароль для проведения операций</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid item xs={12}>
            <TextField
              name="additional_password"
              autoComplete="off"
              label="Пароль"
              placeholder="Введите значение"
              value={values.additional_password}
              onChange={handleChange}
              disabled={changeEmployeeAdditionalPasswordResponse.isLoading}
              error={touched.additional_password && Boolean(errors.additional_password)}
              helperText={touched.additional_password ? errors.additional_password : ''}
              fullWidth
            />
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={changeEmployeeAdditionalPasswordResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={changeEmployeeAdditionalPasswordResponse.isLoading}>
            Сменить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangeEmployeeAdditionalPassword;
